import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";
import { BlogCardItem, BlogDetail } from "@/types/blogs";
import {
  fetchBlogDetail,
  fetchBlogs,
  fetchBlogsNextPage,
  fetchSearchBlogs,
} from "@/api/blogs";

export const useBlogsStore = defineStore("blogsStore", () => {
  const blogList = ref<BlogCardItem[]>([]);
  const blogsDetails = ref<Map<number, BlogDetail>>(new Map());
  const currentBlogDetailId = ref<number | null>(null);
  const nextBlogsPage = ref<string | null>(null);
  const isListLoading = ref(false);
  const isListLoadingReadonly = readonly(isListLoading);

  const hasNextPage = computed(() => nextBlogsPage.value !== null);
  const blogDetail = computed(() => {
    if (currentBlogDetailId.value) {
      return blogsDetails.value.get(currentBlogDetailId.value);
    }
    return null;
  });

  async function fetchBlogsCards(tagId: number | null = null) {
    if (isListLoading.value) return;

    isListLoading.value = true;
    try {
      if (tagId === -1) tagId = null; // -1 is used for "All Articles" tag

      const blogsResponse = await fetchBlogs(tagId);
      blogList.value = blogsResponse.data;
      nextBlogsPage.value = blogsResponse.links.next;
    } catch (error) {
      console.error(error);
    } finally {
      isListLoading.value = false;
    }
  }

  async function loadNextPage() {
    if (!hasNextPage.value || isListLoading.value) return;
    isListLoading.value = true;
    try {
      const res = await fetchBlogsNextPage(<string>nextBlogsPage.value);
      blogList.value.push(...res.data);
      nextBlogsPage.value = res.links.next;
    } finally {
      isListLoading.value = false;
    }
  }

  async function searchBlogs(search: string) {
    if (isListLoading.value) return;
    isListLoading.value = true;

    try {
      const res = await fetchSearchBlogs(search);
      blogList.value = res.data;
      nextBlogsPage.value = res.links.next;
    } finally {
      isListLoading.value = false;
    }
  }

  async function loadBlogDetail(blogId: string) {
    const newBlogId = Number(blogId);
    if (blogsDetails.value.has(newBlogId)) {
      currentBlogDetailId.value = newBlogId;
      return;
    }
    try {
      const blogDetail = await fetchBlogDetail(blogId);
      blogsDetails.value.set(newBlogId, blogDetail);
      currentBlogDetailId.value = newBlogId;
    } catch (error) {
      console.error(error);
    }
  }

  return {
    blogList,
    hasNextPage,
    blogDetail,
    fetchBlogsCards,
    loadBlogDetail,
    loadNextPage,
    searchBlogs,
    isListLoading: isListLoadingReadonly,
    currentBlogDetailId,
  };
});
